import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar, Box, Breadcrumbs, Button, Card, CardContent, Chip, CircularProgress,
  Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Divider, Grid, IconButton, InputAdornment, Link, Paper, Skeleton, Stack,
  Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs,
  TextField, Tooltip, Typography, Select, MenuItem, FormControl
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';
import PublicIcon from '@mui/icons-material/Public';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import KeyIcon from '@mui/icons-material/Key';
import StoreIcon from '@mui/icons-material/Store';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import toast from 'react-hot-toast';
import { clientAPI } from '../../API/clientAPI';
import { userAPI } from '../../API/userAPI';
import { storeAPI } from '../../API/storeAPI';
import useAuth from '../../Context/Hooks/useCustomAuth';
import ClientAvatar from '../../Components/GatewayContagem/ClientAvatar';
import AccessDeniedState from '../../Components/GatewayContagem/AccessDeniedState';
import NotFoundState from '../../Components/GatewayContagem/NotFoundState';
import { isForbiddenError, isNotFoundError } from '../../Utils/apiHelpers';
import { VISIBLE_PROFILES } from '../../Constants/userConstants';
import { TABLE_HEAD_ROW_SX } from '../../Utils/format';

const ClientDetailPage = () => {
  const { t } = useTranslation(['clients', 'common']);
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const isGatewayAdmin = currentUser?.role === 'GATEWAY_ADMIN';

  const [client, setClient] = useState(null);
  const hasApiAccess = client?.planType === 'api' || client?.planType === 'api_platform' || client?.planType === 'API' || client?.planType === 'API_PLATFORM';
  const [loadingClient, setLoadingClient] = useState(true);
  const [isForbidden, setIsForbidden] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);

  // Tab caching & pagination states
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);

  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storesLoaded, setStoresLoaded] = useState(false);

  const [tabValue, setTabValue] = useState('users');

  // Edit Mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', externalId: '', email: '', phone: '', address: '', city: '', postalCode: '', countryCode: '', planType: '', status: ''
  });
  const [savingClient, setSavingClient] = useState(false);

  // API key display state
  const [apiKeyDisplay, setApiKeyDisplay] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [confirmRegenOpen, setConfirmRegenOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const loadClient = useCallback(async () => {
    setLoadingClient(true);
    setIsForbidden(false);
    setIsNotFound(false);
    try {
      const res = await clientAPI.getById(clientId);
      if (isForbiddenError(res)) {
        setIsForbidden(true);
        return;
      }
      if (isNotFoundError(res)) {
        setIsNotFound(true);
        return;
      }
      if (res && !res.error && res.value) {
        setClient(res.value);
        setApiKeyDisplay(res.value.apiKey || '');
        setEditForm({
          name: res.value.name || '',
          externalId: res.value.externalId || '',
          email: res.value.email || '',
          phone: res.value.phone || '',
          address: res.value.address || '',
          city: res.value.city || '',
          postalCode: res.value.postalCode || '',
          countryCode: res.value.countryCode || '',
          planType: res.value.planType || '',
          status: res.value.status || 'Inactive',
        });
      } else {
        toast.error(t('clientsCrud.toast.loadError', 'Erro ao carregar dados do cliente'));
      }
    } catch (err) {
      if (isForbiddenError(null, err)) {
        setIsForbidden(true);
      } else if (isNotFoundError(null, err)) {
        setIsNotFound(true);
      } else {
        toast.error(t('clientsCrud.toast.loadError', 'Erro ao carregar dados do cliente'));
      }
    } finally {
      setLoadingClient(false);
    }
  }, [clientId]);

  const fetchUsers = useCallback(async (pageArg, rowsPerPageArg) => {
    setLoadingUsers(true);
    try {
      const res = await userAPI.list({
        clientId,
        clientIds: clientId ? [clientId] : undefined,
        page: pageArg + 1,
        itemsPerPage: rowsPerPageArg,
        status: ['A', 'P'],
      });
      if (res && !res.error) {
        const items = res.value?.items || [];
        const clientUsers = items.filter((u) => {
          const profile = u.profiles?.[0]?.code ?? 'USER';
          if (profile === 'GATEWAY_ADMIN') {
            return false;
          }
          if (clientId && u.clientId && String(u.clientId).toLowerCase() !== String(clientId).toLowerCase()) {
            return false;
          }
          return true;
        });
        const total = res.value?.totalItems || clientUsers.length;
        setUsers(clientUsers);
        setUsersTotal(total);
        setUsersLoaded(true);
      }
    } finally {
      setLoadingUsers(false);
    }
  }, [clientId]);

  const loadStores = useCallback(async (force = false) => {
    if (storesLoaded && !force) return;
    setLoadingStores(true);
    try {
      const res = await storeAPI.getByClient(clientId);
      if (res && !res.error) {
        setStores(res.value || []);
        setStoresLoaded(true);
      }
    } finally {
      setLoadingStores(false);
    }
  }, [clientId, storesLoaded]);

  // Reset tab cache when clientId changes
  useEffect(() => {
    setUsersLoaded(false);
    setStoresLoaded(false);
    setUsers([]);
    setUsersTotal(0);
    setUsersPage(0);
    setStores([]);
  }, [clientId]);

  // Read edit state from navigation route
  useEffect(() => {
    if (location.state?.edit) {
      setIsEditing(true);
    }
  }, [location]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  useEffect(() => {
    if (tabValue === 'users' && !usersLoaded) {
      fetchUsers(usersPage, usersRowsPerPage);
    } else if (tabValue === 'stores' && !storesLoaded) {
      loadStores();
    }
  }, [tabValue, usersLoaded, storesLoaded, fetchUsers, usersPage, usersRowsPerPage, loadStores]);

  const handleCancelEdit = () => {
    if (client) {
      setEditForm({
        name: client.name || '',
        externalId: client.externalId || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        city: client.city || '',
        postalCode: client.postalCode || '',
        countryCode: client.countryCode || '',
        planType: client.planType || '',
        status: client.status || 'Inactive',
      });
    }
    setIsEditing(false);
  };

  const handleSaveClient = async () => {
    if (!editForm.name.trim()) {
      toast.error(t('clientsCrud.toast.nameRequired', 'Nome do cliente é obrigatório'));
      return;
    }
    setSavingClient(true);
    try {
      const payload = isGatewayAdmin
        ? {
            ...client,
            ...editForm,
          }
        : {
            // Client Admin can only edit details, keep plan type and status from existing client
            ...editForm,
            status: client.status,
            planType: client.planType,
            isActive: client.isActive,
            alertEmailEnabled: client.alertEmailEnabled,
          };

      const res = await clientAPI.update(clientId, payload);
      if (res && !res.error) {
        toast.success(t('clientsCrud.toast.updated', 'Informações do cliente atualizadas'));
        setIsEditing(false);
        loadClient();
      } else {
        toast.error(t('clientsCrud.toast.updateError', 'Erro ao atualizar informações'));
      }
    } finally {
      setSavingClient(false);
    }
  };

  const handleRegenerateApiKey = async () => {
    setConfirmRegenOpen(false);
    setIsRegenerating(true);
    try {
      const res = await clientAPI.regenerateApiKey(clientId);
      if (res && !res.error) {
        const newKey = res.value;
        setApiKeyDisplay(newKey);
        setShowApiKey(true);
        toast.success(t('clientsCrud.toast.apiKeyRegenerated', 'Chave API regenerada com sucesso'));
      } else {
        toast.error(t('clientsCrud.toast.apiKeyRegenerateError', 'Erro ao regenerar chave API'));
      }
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCopyApiKey = () => {
    if (!apiKeyDisplay) return;
    navigator.clipboard.writeText(apiKeyDisplay).then(() => {
      toast.success(t('clientsCrud.toast.apiKeyCopied', 'Chave API copiada para a área de transferência'));
    });
  };

  if (loadingClient) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Breadcrumbs separator="›" sx={{ mb: 3 }}>
          <Skeleton variant="text" width={100} height={20} animation="wave" />
          <Skeleton variant="text" width={140} height={20} animation="wave" />
        </Breadcrumbs>

        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <Skeleton variant="circular" width={44} height={44} animation="wave" />
          <Box sx={{ width: 250 }}>
            <Skeleton variant="text" width={180} height={28} animation="wave" />
            <Skeleton variant="text" width={120} height={18} animation="wave" />
          </Box>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Skeleton variant="text" width={100} height={20} animation="wave" sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Box key={i}>
                      <Skeleton variant="text" width={80} height={16} animation="wave" />
                      <Skeleton variant="text" width={160} height={22} animation="wave" />
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Card variant="outlined">
              <CardContent>
                <Skeleton variant="rounded" width="100%" height={36} animation="wave" sx={{ mb: 2, borderRadius: 1 }} />
                <Stack spacing={1.5}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant="rounded" width="100%" height={48} animation="wave" sx={{ borderRadius: 1 }} />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (isForbidden) return <AccessDeniedState backPath="/Gateway/Clients" />;

  if (isNotFound || !client) return <NotFoundState backPath="/Gateway/Clients" />;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator="›" sx={{ mb: 3 }}>
        <Link component={Typography} onClick={() => navigate('/Gateway/Clients')} sx={{ cursor: 'pointer', color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {t('clients:clientDetail.breadcrumb', 'Clientes & Lojas')}
        </Link>
        <Typography color="text.primary" fontWeight={500}>{client.name}</Typography>
      </Breadcrumbs>

      {/* Header section */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '70%' }}>
          <IconButton onClick={() => navigate('/Gateway/Clients')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <ClientAvatar name={client.name} size={44} />
          <Box sx={{ width: '100%' }}>
            <Typography variant="h5" fontWeight={700}>{client.name}</Typography>
            <Typography variant="caption" color="text.secondary">{t('clients:clientDetail.externalId', 'ID Externo')}: {client.externalId || '—'}</Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5}>
          {isEditing ? (
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<CloseIcon />}
                onClick={handleCancelEdit}
                disabled={savingClient}
              >
                {t('clients:clientDetail.cancel', 'Cancelar')}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveClient}
                disabled={savingClient}
              >
                {savingClient ? t('clients:clientDetail.saving', 'A guardar…') : t('clients:clientDetail.save', 'Guardar')}
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
            >
              {t('clients:clientDetail.editClient', 'Editar Cliente')}
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {/* Left column: Info card */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2}>
                {t('clients:clientDetail.generalData', 'DADOS GERAIS')}
              </Typography>
              <Stack spacing={2}>
                {/* State (Status) */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <BusinessIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('clients:clientDetail.status', 'Estado')}</Typography>
                    {isEditing && isGatewayAdmin ? (
                      <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                        <Select
                          value={editForm.status}
                          onChange={(e) => setEditForm(f => ({ ...f, status: e.target.value }))}
                        >
                          <MenuItem value="Active">{t('clients:status.active', 'Ativo')}</MenuItem>
                          <MenuItem value="Inactive">{t('clients:status.inactive', 'Inativo')}</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        label={client.status === 'Active' ? t('clients:status.active', 'Ativo') : t('clients:status.inactive', 'Inativo')}
                        color={client.status === 'Active' ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 600, mt: 0.5 }}
                      />
                    )}
                  </Box>
                </Stack>

                {/* Plan Type */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <KeyIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('clients:clientDetail.planContracted', 'Plano contratado')}</Typography>
                    {isEditing && isGatewayAdmin ? (
                      <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                        <Select
                          value={editForm.planType}
                          onChange={(e) => setEditForm(f => ({ ...f, planType: e.target.value }))}
                        >
                          <MenuItem value="">{t('clients:clientDetail.none', 'Nenhum')}</MenuItem>
                          <MenuItem value="api">API</MenuItem>
                          <MenuItem value="platform">{t('clients:chips.platform', 'Plataforma')}</MenuItem>
                          <MenuItem value="api_platform">{t('clients:filters.apiPlatform', 'API + Plataforma')}</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Stack direction="row" spacing={0.5} mt={0.5}>
                        {!client.planType && (
                          <Typography variant="body2" color="text.disabled">{t('clients:clientDetail.none', 'Nenhum')}</Typography>
                        )}
                        {(client.planType === 'api' || client.planType === 'api_platform') && (
                          <Chip
                            label="API"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, px: 0.25 }}
                          />
                        )}
                        {(client.planType === 'platform' || client.planType === 'api_platform') && (
                          <Chip
                            label={t('clients:chips.platform', 'Plataforma')}
                            size="small"
                            color="primary"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, px: 0.25 }}
                          />
                        )}
                      </Stack>
                    )}
                  </Box>
                </Stack>

                <Divider />

                {/* Email */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <EmailIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('clients:clientDetail.contactEmail', 'Email de contacto')}</Typography>
                    {isEditing ? (
                      <TextField
                        value={editForm.email}
                        onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                        size="small"
                        fullWidth
                        type="email"
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="body2">{client.email || '—'}</Typography>
                    )}
                  </Box>
                </Stack>

                {/* Phone */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PhoneIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('clients:clientDetail.phone', 'Telefone')}</Typography>
                    {isEditing ? (
                      <TextField
                        value={editForm.phone}
                        onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                        size="small"
                        fullWidth
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="body2">{client.phone || '—'}</Typography>
                    )}
                  </Box>
                </Stack>

                {/* Address (Morada) */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <HomeIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('clients:clientDetail.address', 'Morada')}</Typography>
                    {isEditing ? (
                      <TextField
                        value={editForm.address}
                        onChange={(e) => setEditForm(f => ({ ...f, address: e.target.value }))}
                        size="small"
                        fullWidth
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="body2">{client.address || '—'}</Typography>
                    )}
                  </Box>
                </Stack>

                {/* City (Cidade) */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <LocationOnIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('clients:clientDetail.city', 'Cidade')}</Typography>
                    {isEditing ? (
                      <TextField
                        value={editForm.city}
                        onChange={(e) => setEditForm(f => ({ ...f, city: e.target.value }))}
                        size="small"
                        fullWidth
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="body2">{client.city || '—'}</Typography>
                    )}
                  </Box>
                </Stack>

                {/* Postal Code (Código Postal) */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <MarkunreadMailboxIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('clients:clientDetail.postalCode', 'Código postal')}</Typography>
                    {isEditing ? (
                      <TextField
                        value={editForm.postalCode}
                        onChange={(e) => setEditForm(f => ({ ...f, postalCode: e.target.value }))}
                        size="small"
                        fullWidth
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="body2">{client.postalCode || '—'}</Typography>
                    )}
                  </Box>
                </Stack>

                {/* Country Code (Código do País) */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PublicIcon color="action" fontSize="small" />
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{t('clients:clientDetail.countryCode', 'Código do País (ISO)')}</Typography>
                    {isEditing ? (
                      <TextField
                        value={editForm.countryCode}
                        onChange={(e) => setEditForm(f => ({ ...f, countryCode: e.target.value.toUpperCase() }))}
                        placeholder="PT"
                        inputProps={{ maxLength: 2 }}
                        size="small"
                        fullWidth
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <Typography variant="body2">{client.countryCode ? client.countryCode.toUpperCase() : '—'}</Typography>
                    )}
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ minHeight: 400, border: 'none' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} aria-label="Abas de detalhe do cliente">
                    <Tab value="users" label={t('clients:clientDetail.users', 'Utilizadores')} icon={<GroupIcon fontSize="small" />} iconPosition="start" />
                    {hasApiAccess && (
                      <Tab value="api" label={t('clients:clientDetail.apiIntegration', 'Integração (Chave API)')} icon={<KeyIcon fontSize="small" />} iconPosition="start" />
                    )}
                    <Tab value="stores" label={t('clients:clientDetail.stores', 'Lojas')} icon={<StoreIcon fontSize="small" />} iconPosition="start" />
                  </Tabs>
                </Box>

                {tabValue === 'users' && (() => {
                  const currentUserRole = currentUser?.role ?? 'USER';
                  const allowedProfs = (VISIBLE_PROFILES[currentUserRole] ?? ['CLIENT_ADMIN', 'USER'])
                    .filter((p) => p !== 'GATEWAY_ADMIN');
                  const visibleUsers = users.filter((u) => {
                    const profile = u.profiles?.[0]?.code ?? 'USER';
                    if (profile === 'GATEWAY_ADMIN' || !allowedProfs.includes(profile)) {
                      return false;
                    }
                    if (clientId && u.clientId && String(u.clientId).toLowerCase() !== String(clientId).toLowerCase()) {
                      return false;
                    }
                    return true;
                  });

                  return (
                    <Box sx={{ p: visibleUsers.length > 0 && !loadingUsers ? 0 : 3 }}>
                      {loadingUsers ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress size={30} />
                        </Box>
                      ) : visibleUsers.length > 0 ? (
                        <>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={TABLE_HEAD_ROW_SX}>
                                  <TableCell>{t('clients:clientDetail.tableUser', 'Utilizador')}</TableCell>
                                  <TableCell>{t('clients:clientDetail.tableEmail', 'Email')}</TableCell>
                                  <TableCell>{t('clients:clientDetail.tableProfileRole', 'Perfil / Cargo')}</TableCell>
                                  <TableCell>{t('clients:clientDetail.tableStatus', 'Estado')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {visibleUsers.map((u) => (
                                  <TableRow
                                    key={u.id}
                                    hover
                                    onClick={() => navigate(`/Gateway/Users/${u.id}`)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    <TableCell>
                                      <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: '#1B75BB', fontWeight: 700 }}>
                                          {u.name
                                            ? u.name
                                                .split(' ')
                                                .map((n) => n[0])
                                                .join('')
                                                .substring(0, 2)
                                                .toUpperCase()
                                            : '?'}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                                      </Stack>
                                    </TableCell>
                                    <TableCell>{u.email}</TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="text.primary" fontWeight={500}>
                                        {u.profiles?.[0]?.code === 'GATEWAY_ADMIN'
                                          ? t('clients:clientDetail.gatewayAdmin', 'Administração Gateway')
                                          : u.profiles?.[0]?.code === 'CLIENT_ADMIN'
                                          ? t('clients:clientDetail.clientAdmin', 'Administração Cliente')
                                          : t('clients:clientDetail.standardUser', 'Utilizador')}
                                      </Typography>
                                      {u.jobTitle && (
                                        <Typography variant="caption" color="text.secondary">
                                          {u.jobTitle}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={u.sysStatus === 'A' ? t('clients:status.active', 'Ativo') : u.sysStatus === 'P' ? t('common:status.pending', 'Pendente') : t('clients:status.inactive', 'Inativo')}
                                        size="small"
                                        color={u.sysStatus === 'A' ? 'success' : u.sysStatus === 'P' ? 'warning' : 'default'}
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: 10 }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={usersTotal}
                            rowsPerPage={usersRowsPerPage}
                            page={usersPage}
                            onPageChange={(_, newPage) => {
                              setUsersPage(newPage);
                              fetchUsers(newPage, usersRowsPerPage);
                            }}
                            onRowsPerPageChange={(e) => {
                              const newRowsPerPage = parseInt(e.target.value, 10);
                              setUsersRowsPerPage(newRowsPerPage);
                              setUsersPage(0);
                              fetchUsers(0, newRowsPerPage);
                            }}
                          />
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary" align="center" py={4}>
                          {t('clients:clientDetail.noUsers', 'Nenhum utilizador associado a este cliente.')}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}

                {/* TAB API Key: Only rendered if plan supports API */}
                {hasApiAccess && tabValue === 'api' && (
                  <Box p={3}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={1}>
                      {t('clients:clientDetail.apiTitle', 'Configuração da Integração via API Pública')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      {t('clients:clientDetail.apiSubtitle', 'Use esta chave API para autenticar as requisições da sua organização nos nossos endpoints públicos de ingestão e recolha de contagens.')}
                    </Typography>

                    <Box sx={{ maxWidth: 500 }}>
                      <TextField
                        value={apiKeyDisplay}
                        type={showApiKey ? 'text' : 'password'}
                        size="small"
                        fullWidth
                        placeholder="—"
                        helperText={t('clients:clientDetail.apiSecretHint', 'Mantenha esta chave secreta e partilhe-a apenas em ambientes seguros.')}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={showApiKey ? t('clients:clientDetail.hide', 'Ocultar') : t('clients:clientDetail.show', 'Mostrar')}>
                                <IconButton
                                  size="small"
                                  onClick={() => setShowApiKey(v => !v)}
                                  disabled={!apiKeyDisplay}
                                >
                                  {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('clients:clientDetail.copy', 'Copiar')}>
                                <IconButton
                                  size="small"
                                  onClick={handleCopyApiKey}
                                  disabled={!apiKeyDisplay}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {isGatewayAdmin && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={isRegenerating
                            ? <CircularProgress size={14} color="inherit" />
                            : <RefreshIcon fontSize="small" />}
                          onClick={() => setConfirmRegenOpen(true)}
                          disabled={isRegenerating}
                          sx={{ mt: 2 }}
                        >
                          {isRegenerating ? t('clients:clientDetail.regenerating', 'A regenerar…') : t('clients:clientDetail.regenerateApiKey', 'Regenerar Chave API')}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

                {/* TAB Stores */}
                {tabValue === 'stores' && (
                  <Box sx={{ p: stores.length > 0 && !loadingStores ? 0 : 3 }}>
                {loadingStores ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : stores.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={TABLE_HEAD_ROW_SX}>
                          <TableCell>{t('clients:clientDetail.tableStore', 'Loja')}</TableCell>
                          <TableCell>{t('clients:clientDetail.tableCity', 'Cidade')}</TableCell>
                          <TableCell>{t('clients:clientDetail.tableArea', 'Área')}</TableCell>
                          <TableCell>{t('clients:clientDetail.tableOpenedYear', 'Ano Abertura')}</TableCell>
                          <TableCell>{t('clients:clientDetail.tableStatusHeader', 'Estado')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stores.map((s) => (
                          <TableRow key={s.id} hover onClick={() => navigate(`/Gateway/Stores/${s.id}`)} sx={{ cursor: 'pointer' }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{s.externalId || '—'}</Typography>
                            </TableCell>
                            <TableCell>{s.city || '—'}</TableCell>
                            <TableCell>{s.area ? `${s.area} m²` : '—'}</TableCell>
                            <TableCell>{s.openedYear || '—'}</TableCell>
                            <TableCell>
                              <Chip
                                label={s.status === 'Active' ? t('clients:status.active', 'Ativo') : t('clients:status.inactive', 'Inativo')}
                                size="small"
                                color={s.status === 'Active' ? 'success' : 'default'}
                                variant="outlined"
                                sx={{ height: 20 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center" py={4}>
                    {t('clients:clientDetail.noStores', 'Nenhuma loja registada para este cliente.')}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
      {/* Confirmation Modal for API Key Regeneration */}
      <Dialog
        open={confirmRegenOpen}
        onClose={() => setConfirmRegenOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, pb: 1 }}>
          <WarningAmberRoundedIcon color="warning" />
          {t('clients:clientDetail.confirmRegenTitle', 'Confirmar Regeneração')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            {t('clients:clientDetail.confirmRegenPrompt', 'Tem a certeza que deseja regenerar a chave API?')}
            <br />
            <strong>{t('clients:clientDetail.confirmRegenWarning', 'Aplicações integradas deixarão de funcionar até que a nova chave seja atualizada nos respetivos sistemas.')}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            size="small"
            onClick={() => setConfirmRegenOpen(false)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t('clients:clientDetail.cancel', 'Cancelar')}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            onClick={handleRegenerateApiKey}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('clients:clientDetail.regenerateApiKey', 'Regenerar')}
          </Button>
        </DialogActions>
      </Dialog>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ClientDetailPage;
